import { derived, get, type Readable, type Writable, type Updater, type Unsubscriber } from 'svelte/store';

type Store<T> = Readable<T> | Writable<T>;
type StoreValue<T> = T extends Store<infer U> ? U : never;

type DeriveFn<T, U> = (
	values: T extends Store<infer V> ? V : T extends Store<infer V>[] ? V[] : never,
	set: (value: U) => void,
	update: (fn: Updater<U>) => void
) => U | void;

type ReflectFn<T, U> = (
	reflecting: U,
	oldValues: T extends Store<infer V> ? V : T extends Store<infer V>[] ? V[] : never
) => T extends Store<infer V> ? V : T extends Store<infer V>[] ? V[] : never;

/**
 * @external Store
 * @see [Svelte stores](https://svelte.dev/docs#component-format-script-4-prefix-stores-with-$-to-access-their-values-store-contract)
 */

/**
 * Create a store similar to [Svelte's `derived`](https://svelte.dev/docs#run-time-svelte-store-writable),
 * but which has its own `set` and `update` methods and can send values back to the origin stores.
 * [Read more...](https://github.com/PixievoltNo1/svelte-writable-derived#default-export-writablederived)
 *
 * @param {Store|Store[]} origins One or more stores to derive from. Same as
 * [`derived`](https://svelte.dev/docs#run-time-svelte-store-writable)'s 1st parameter.
 * @param {!Function} derive The callback to determine the derived value. Same as
 * [`derived`](https://svelte.dev/docs#run-time-svelte-store-writable)'s 2nd parameter.
 * @param {!Function} reflect Called when the derived store gets a new value via its `set` or
 * `update` methods, and determines new values for the origin stores.
 * [Read more...](https://github.com/PixievoltNo1/svelte-writable-derived#new-parameter-reflect)
 * @param [initial] The new store's initial value. Same as
 * [`derived`](https://svelte.dev/docs#run-time-svelte-store-writable)'s 3rd parameter.
 *
 * @returns {Store} A writable store.
 */
export default function writableDerived<T extends Store<any> | Store<any>[], U>(
	origins: T,
	derive: DeriveFn<T, U>,
	reflect: ReflectFn<T, U>,
	initial?: U
): Writable<U> {
	let childDerivedSetter: (value: U) => void;
	let originValues: StoreValue<T>;
	let blockNextDerive = false;
	const reflectOldValues = reflect.length >= 2;
	const wrappedDerive = (got: StoreValue<T>, set: (value: U) => void, update: (fn: Updater<U>) => void): void | Unsubscriber => {
		childDerivedSetter = set;
		if (reflectOldValues) {
			originValues = got;
		}
		if (!blockNextDerive) {
			const returned = derive(got, set, update);
			if (derive.length < 2) {
				set(returned as U);
			} else {
				return returned as Unsubscriber;
			}
		}
		blockNextDerive = false;
	};
	const childDerived = derived<StoreValue<T>, U>(origins, wrappedDerive, initial);

	const singleOrigin = !Array.isArray(origins);
	function doReflect(reflecting: U) {
		const setWith = reflect(reflecting, originValues);
		if (singleOrigin) {
			blockNextDerive = true;
			(origins as Writable<StoreValue<T>>).set(setWith as StoreValue<T>);
		} else {
			(setWith as StoreValue<T>[]).forEach((value, i) => {
				blockNextDerive = true;
				(origins as Writable<StoreValue<T>>[])[i].set(value);
			});
		}
		blockNextDerive = false;
	}

	let tryingSet = false;
	function update(fn: Updater<U>) {
		let isUpdated = false;
		let mutatedBySubscriptions = false;
		let oldValue: U;
		let newValue: U;
		if (tryingSet) {
			newValue = fn(get(childDerived));
			childDerivedSetter(newValue);
			return;
		}
		const unsubscribe = childDerived.subscribe((value) => {
			if (!tryingSet) {
				oldValue = value;
			} else if (!isUpdated) {
				isUpdated = true;
			} else {
				mutatedBySubscriptions = true;
			}
		});
		newValue = fn(oldValue!);
		tryingSet = true;
		childDerivedSetter(newValue);
		unsubscribe();
		tryingSet = false;
		if (mutatedBySubscriptions) {
			newValue = get(childDerived);
		}
		if (isUpdated) {
			doReflect(newValue);
		}
	}
	return {
		subscribe: childDerived.subscribe,
		set(value: U) {
			update(() => value);
		},
		update
	};
}

/**
 * Create a store for a property value in an object contained in another store.
 * [Read more...](https://github.com/PixievoltNo1/svelte-writable-derived#named-export-propertystore)
 *
 * @param {Store} origin The store containing the object to get/set from.
 * @param {string|number|symbol|Array<string|number|symbol>} propName The property to get/set, or a path of
 * properties in nested objects.
 *
 * @returns {Store} A writable store.
 */
export function propertyStore<T extends Record<string, any>, K extends keyof T>(
	origin: Writable<T>,
	propName: K | K[]
): Writable<T[K]> {
	if (!Array.isArray(propName)) {
		return writableDerived(
			origin,
			(object) => object[propName],
			(reflecting, object) => {
				object[propName] = reflecting;
				return object;
			},
			undefined
		);
	} else {
		const props = propName.concat();
		return writableDerived(
			origin,
			(value) => {
				let result: any = value;
				for (let i = 0; i < props.length; ++i) {
					result = result[props[i]];
				}
				return result;
			},
			(reflecting, object) => {
				let target = object;
				for (let i = 0; i < props.length - 1; ++i) {
					target = target[props[i]];
				}
				target[props[props.length - 1]] = reflecting;
				return object;
			},
			undefined
		);
	}
}
